import { useSelect } from "downshift";
import {
  CheckIcon,
  ChevronPaginationDownIcon,
  ChevronUp,
} from "src/assets/icons";
import {
  Container,
  List,
  ListTitle,
  ListItem,
  LeftIconSlot,
  TextSlot,
  RightIconSlot,
  ButtonIcon,
  LeftSideGroup,
  RightSideGroup,
} from "src/components/Dropdown.styles";
import { GhostButton } from "src/components/Buttons";

interface MenuItemsObjType {
  [key: string]: string | { label: string; icon?: React.ReactNode };
}

export default ({
  title,
  menuItemsObj,
  selectedItem,
  onSelectedItemChange,
  displayText,
  hideCheckmark,
  customTrigger,
}: {
  displayText?: string;
  title?: string;
  menuItemsObj: MenuItemsObjType;
  selectedItem: string;
  onSelectedItemChange: (selectedItem: string) => void;
  hideCheckmark?: boolean;
  customTrigger?: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
}) => {
  const getItemLabel = (
    item: string | { label: string; icon?: React.ReactNode },
  ): string => {
    return typeof item === "string" ? item : item.label;
  };

  const getItemIcon = (
    item: string | { label: string; icon?: React.ReactNode },
  ): React.ReactNode => {
    return typeof item === "string" ? null : item.icon;
  };
  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect({
      items: Object.keys(menuItemsObj),
      selectedItem: selectedItem,
      onSelectedItemChange: ({ selectedItem }) =>
        onSelectedItemChange(selectedItem ?? ""),
    });

  return (
    <Container>
      {customTrigger ? (
        <div {...getToggleButtonProps()}>
          {typeof customTrigger === "function"
            ? customTrigger(isOpen)
            : customTrigger}
        </div>
      ) : (
        <GhostButton
          fontSize="12px"
          padding="6px 10px 6px 12px"
          {...getToggleButtonProps()}
        >
          {selectedItem &&
            selectedItem in menuItemsObj &&
            getItemIcon(menuItemsObj[selectedItem]) && (
              <ButtonIcon>{getItemIcon(menuItemsObj[selectedItem])}</ButtonIcon>
            )}
          <span>
            {displayText ||
              (selectedItem && selectedItem in menuItemsObj
                ? getItemLabel(menuItemsObj[selectedItem])
                : "Select...")}
          </span>
          {isOpen ? <ChevronUp /> : <ChevronPaginationDownIcon />}
        </GhostButton>
      )}

      <List {...getMenuProps()} isOpen={isOpen}>
        {title && <ListTitle>{title}</ListTitle>}
        {isOpen &&
          Object.entries(menuItemsObj).map(([key, item], index) => {
            const hasLeftIcon = !!getItemIcon(item);
            return (
              <ListItem key={key} {...getItemProps({ item: key, index })}>
                <LeftSideGroup>
                  {hasLeftIcon && (
                    <LeftIconSlot>{getItemIcon(item)}</LeftIconSlot>
                  )}
                  <TextSlot hasLeftIcon={hasLeftIcon}>
                    {getItemLabel(item)}
                  </TextSlot>
                </LeftSideGroup>
                <RightSideGroup>
                  <RightIconSlot>
                    {!hideCheckmark && selectedItem === key && <CheckIcon />}
                  </RightIconSlot>
                </RightSideGroup>
              </ListItem>
            );
          })}
      </List>
    </Container>
  );
};
